import React from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Avatar,
  Button,
} from "@mui/material";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import FavoriteIcon from "@mui/icons-material/Favorite";
import Colorapps from "../../../configcss/color";
import Navbar from "../../../Components/Navbar";
import Footer from "../../../Components/Footer";

// Sample logos (replace with actual imports or URLs)
const NST_LOGO = "path/to/nst-logo.png"; // Replace with actual NST logo path
const LIHS_LOGO = "path/to/lihs-logo.png"; // Replace with actual LIHS logo path

// Define professional colors
const colors = {
  primary: "#F5A623", // Orange from the image
  secondary: "#FFFFFF", // White
  textPrimary: "#333333", // Darker text for readability
  textSecondary: "#FFFFFF", // White text for contrast
  background: "#F9F9F9", // Light gray background
  accent: "#E59400", // Darker orange for hover effects
};

// Features data
const medicineFeatures = [
  {
    icon: <LocalPharmacyIcon fontSize="large" />,
    title: "Essential Medicines",
    description:
      "Free access to essential medicines for individuals who need help affording healthcare.",
  },
  {
    icon: <VolunteerActivismIcon fontSize="large" />,
    title: "Community Collaboration",
    description:
      "We work closely with local pharmacies and volunteers to distribute medicine efficiently.",
  },
  {
    icon: <FavoriteIcon fontSize="large" />,
    title: "Compassionate Care",
    description:
      "Everyone deserves healthcare with dignity, compassion, and human connection.",
  },
];

// Custom CSS for animations and glassmorphism
const customStyles = `
  @keyframes fadeIn {
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 1s ease-out forwards;
  }
  .glass-card {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .glass-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
  .cta-button {
    transition: all 0.3s ease;
  }
  .cta-button:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  @media (max-width: 600px) {
    .hero-title {
      font-size: 1.5rem !important;
    }
    .hero-text {
      font-size: 0.9rem !important;
    }
  }
`;

function FreeMedicine() {
  return (
    <>
      <Navbar />
      <Box sx={{ backgroundColor: colors.background }}>
        {/* Inject custom styles */}
        <style>{customStyles}</style>

        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            height: { xs: "60vh", md: "80vh" },
            background: `url("https://images.unsplash.com/photo-1588776814546-ec7e53b2c2e5") center/cover no-repeat`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7))",
            }}
          />
          <Box
            sx={{
              position: "relative",
              color: colors.textSecondary,
              textAlign: "center",
              zIndex: 1,
              px: { xs: 2, md: 4 },
            }}
            className="fade-in"
          >
            <Typography
              variant="h3"
              fontWeight="bold"
              gutterBottom
              sx={{
                fontFamily: "Poppins, sans-serif",
                textShadow: "0px 2px 10px rgba(0,0,0,0.8)",
                fontSize: { xs: "2rem", md: "3.5rem" },
              }}
              className="hero-title"
            >
              Healing with Heart
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "1rem", md: "1.3rem" },
                fontFamily: "Roboto, sans-serif",
                textShadow: "0px 2px 5px rgba(0,0,0,0.8)",
                maxWidth: "800px",
                mx: "auto",
                lineHeight: 1.6,
              }}
              className="hero-text"
            >
              “He who relieves the hardship of a believer in this world, Allah will relieve his hardship on the Day of Judgment.”
              <br />
              <strong>[Muslim]</strong>
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 4,
                backgroundColor: colors.primary,
                color: colors.textSecondary,
                fontWeight: "bold",
                padding: "12px 40px",
                borderRadius: "30px",
                fontSize: "1.1rem",
                "&:hover": {
                  backgroundColor: colors.accent,
                },
              }}
              href="#medicine-program"
              className="cta-button"
            >
              Learn More
            </Button>
          </Box>
        </Box>

        {/* Program Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            backgroundColor: colors.secondary,
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          }}
          id="medicine-program"
        >
          <Container maxWidth="lg">
            {/* Logos Section */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                mb: 6,
                flexWrap: "wrap",
              }}
              className="fade-in"
            >
              <Box
                component="img"
                src={NST_LOGO}
                alt="NST Logo"
                sx={{ height: { xs: "50px", md: "80px" }, objectFit: "contain" }}
              />
              <Box
                component="img"
                src={LIHS_LOGO}
                alt="LIHS Logo"
                sx={{ height: { xs: "50px", md: "80px" }, objectFit: "contain" }}
              />
            </Box>

            <Box textAlign="center" mb={8}>
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  color: colors.textPrimary,
                  fontSize: { xs: "2rem", md: "3rem" },
                }}
                className="fade-in"
              >
                Our Free Medicine Program
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  color: colors.textPrimary,
                  fontStyle: "italic",
                  maxWidth: "600px",
                  mx: "auto",
                  fontSize: { xs: "1rem", md: "1.2rem" },
                }}
              >
                Ensuring access to life-saving medication for every person in need.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {medicineFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      padding: 4,
                      textAlign: "center",
                      borderRadius: "20px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                    className="glass-card fade-in"
                    style={{ animationDelay: `${index * 0.2}s` }}
                    role="article"
                    aria-label={feature.title}
                  >
                    <Avatar
                      sx={{
                        bgcolor: colors.primary,
                        mb: 3,
                        width: 70,
                        height: 70,
                        color: colors.textSecondary,
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      gutterBottom
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        color: colors.textPrimary,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        color: colors.textPrimary,
                        lineHeight: 1.6,
                        fontSize: "1rem",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* CTA Section */}
            <Box
              sx={{
                mt: 10,
                p: { xs: 4, md: 6 },
                backgroundColor: colors.primary,
                borderRadius: "20px",
                textAlign: "center",
                color: colors.textSecondary,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
              className="fade-in"
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Want to Contribute or Register?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  mb: 3,
                  maxWidth: "600px",
                  mx: "auto",
                  fontSize: "1.1rem",
                }}
              >
                Join us in making healthcare accessible to all. Your support can save lives.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: colors.secondary,
                  color: colors.textPrimary,
                  fontWeight: "bold",
                  padding: "12px 40px",
                  borderRadius: "30px",
                  fontSize: "1.1rem",
                  "&:hover": {
                    backgroundColor: colors.background,
                  },
                }}
                className="cta-button"
              >
                Contact Us
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
      <Footer />
    </>
  );
}

export default FreeMedicine;